/* ============================================
   LeadMaterialTab — el material comercial del servicio

   Lo que se le puede mostrar o mandar al cliente sale del
   servicio asignado a la oportunidad: la API ya adjunta
   `recursos[]` a cada servicio (los propios más los de su
   línea), así que acá no se consulta nada nuevo.

   DOS BLOQUES SEPARADOS, y es la razón de ser del
   componente: lo público (lo que se le manda al cliente) y
   lo interno (guiones, argumentarios). El botón de envío
   existe SOLO en el bloque público — no es un `disabled`
   ni un condicional dentro de la misma fila de botones,
   para que un guion de presentador no pueda salir por un
   clic de más.
   ============================================ */
import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useApi } from '@/hooks/useApi'
import { useLeadConversaciones } from '@/hooks/useLeadConversaciones'
import { Badge, Button, Modal } from '@/components/ui'
import LeadAdjuntos from './LeadAdjuntos'
import type { Lead, Servicio, ServicioRecurso, TipoRecurso } from '@/lib/types'
import {
  IconExternalLink, IconCopy, IconCheck, IconWhatsApp, IconLock,
  IconPackage, IconAlertCircle, IconPaperclip,
} from '@/components/icons/Icons'

interface LeadMaterialTabProps {
  lead: Lead
  /** El servicio asignado, ya resuelto por la ficha. `null` si no hay o no cargó. */
  servicio: Servicio | null
  cargandoCatalogo: boolean
}

const TIPO_LABEL: Record<TipoRecurso, string> = {
  presentacion: 'Presentación',
  documento: 'Documento',
  pdf: 'PDF',
  plantilla: 'Plantilla',
  enlace: 'Enlace',
  imagen: 'Imagen',
  video: 'Video',
}

/**
 * Los decks curados viven en el repo y se registran con ruta relativa
 * (`/presentaciones/x.html`). Lo que se copia o se manda tiene que abrir
 * fuera del ERP, así que siempre sale absoluto.
 */
function urlAbsoluta(url: string): string {
  if (/^https?:\/\//i.test(url)) return url
  return `${window.location.origin}${url.startsWith('/') ? '' : '/'}${url}`
}

export default function LeadMaterialTab({ lead, servicio, cargandoCatalogo }: LeadMaterialTabProps) {
  const api = useApi()
  const { conversaciones } = useLeadConversaciones(lead)

  const [copiado, setCopiado] = useState<number | null>(null)
  const [aEnviar, setAEnviar] = useState<ServicioRecurso | null>(null)
  const [texto, setTexto] = useState('')
  const [destinoId, setDestinoId] = useState<number | null>(null)
  const [enviando, setEnviando] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [enviado, setEnviado] = useState<number | null>(null)

  // Solo WhatsApp: es el canal por el que el ERP escribe (`/api/chat/send`
  // acepta whatsapp y web_chat, y un chat web del sitio no es un hilo al que
  // se le mande una propuesta).
  const chats = useMemo(
    () => conversaciones.filter(c => c.canal === 'whatsapp'),
    [conversaciones]
  )

  /** Nombre por contacto_id, para que el chat destino se reconozca por persona. */
  const nombrePorContacto = useMemo(() => {
    const map: Record<number, string> = {}
    ;(lead.contactos || []).forEach(c => {
      map[c.contacto_id] = `${c.nombre} ${c.apellido || ''}`.trim()
    })
    return map
  }, [lead.contactos])

  const recursos = useMemo(
    () => (Array.isArray(servicio?.recursos) ? servicio!.recursos : []),
    [servicio]
  )
  const publicos = recursos.filter(r => r.publico)
  const internos = recursos.filter(r => !r.publico)

  async function copiar(r: ServicioRecurso) {
    try {
      await navigator.clipboard.writeText(urlAbsoluta(r.url))
      setCopiado(r.id)
      window.setTimeout(() => setCopiado(actual => (actual === r.id ? null : actual)), 1800)
    } catch {
      setError('No se pudo copiar el enlace')
    }
  }

  function abrirEnvio(r: ServicioRecurso) {
    setAEnviar(r)
    setTexto(`Te comparto ${r.titulo}:\n${urlAbsoluta(r.url)}`)
    setDestinoId(chats[0]?.id ?? null)
    setError(null)
  }

  async function enviar() {
    if (!aEnviar || !destinoId) return
    setEnviando(true)
    setError(null)
    try {
      await api.post('/api/chat/send', {
        conversacion_id: destinoId,
        tipo: 'texto',
        contenido: texto,
      })
      setEnviado(aEnviar.id)
      setAEnviar(null)
      window.setTimeout(() => setEnviado(null), 3000)
    } catch (err: any) {
      setError(err?.message || 'No se pudo enviar el mensaje')
    } finally {
      setEnviando(false)
    }
  }

  /** Tarjeta de recurso. `acciones` las pone cada bloque: son lo que los distingue. */
  function Tarjeta({ r, interno }: { r: ServicioRecurso; interno?: boolean }) {
    return (
      <article className="ld-material__card">
        <header className="ld-material__card-head">
          <Badge variant="info">{TIPO_LABEL[r.tipo] || r.tipo}</Badge>
          {interno && <Badge variant="interno"><IconLock size={10} /> Interno</Badge>}
          {r.categoria && <span className="ld-material__linea">De la línea {r.categoria}</span>}
        </header>

        <h5 className="ld-material__titulo">{r.titulo}</h5>
        {r.descripcion && <p className="ld-material__desc">{r.descripcion}</p>}

        <div className="ld-material__acciones">
          <a
            className="btn btn-secondary btn-sm"
            href={urlAbsoluta(r.url)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <IconExternalLink size={13} /> Abrir
          </a>
          <Button variant="secondary" size="sm" onClick={() => copiar(r)}>
            {copiado === r.id
              ? <><IconCheck size={13} /> Copiado</>
              : <><IconCopy size={13} /> Copiar enlace</>}
          </Button>
          {/* El envío solo se arma para el material público. El bloque interno
              no llega nunca a esta rama. */}
          {!interno && chats.length > 0 && (
            <Button variant="primary" size="sm" onClick={() => abrirEnvio(r)}>
              {enviado === r.id
                ? <><IconCheck size={13} /> Enviado</>
                : <><IconWhatsApp size={13} /> Enviar por WhatsApp</>}
            </Button>
          )}
        </div>
      </article>
    )
  }

  /*
    El estado del CATÁLOGO, que es solo una parte de la pestaña. Antes cada uno
    de estos casos hacía un `return` temprano y se llevaba puesta la pantalla
    entera: sin servicio asignado, la pestaña Material no ofrecía nada que
    hacer — ni siquiera subir un archivo, que es lo primero que uno busca ahí.
  */
  const notaDelCatalogo = (() => {
    if (!lead.servicio_id) {
      return (
        <>
          <p>Esta oportunidad todavía no tiene un servicio asignado.</p>
          <span className="ld-material__pista">
            Elegilo en <strong>Overview → Detalles</strong> y acá aparece su material comercial.
          </span>
        </>
      )
    }
    if (cargandoCatalogo) return <p>Cargando el material del servicio...</p>
    if (!servicio) {
      return (
        <>
          <p>El servicio asignado ya no está en el catálogo.</p>
          <span className="ld-material__pista">Elegí otro en <strong>Overview → Detalles</strong>.</span>
        </>
      )
    }
    if (recursos.length === 0) {
      return (
        <>
          <p>Este servicio no tiene material cargado.</p>
          <span className="ld-material__pista">
            Subí la presentación, el PDF o el guion desde la ficha de{' '}
            <Link to="/servicios" className="ld-enlace">{servicio.nombre}</Link> y aparece acá.
          </span>
        </>
      )
    }
    return null
  })()

  return (
    <div className="ld-material">
      {error && <div className="ld-material__error">{error}</div>}

      {/* ─── Archivos de esta oportunidad ───
          Van primero porque son los únicos que se pueden SUBIR acá: el resto
          de la pestaña es el catálogo, que es de lectura. Vivían en
          Overview → Documentos, donde nadie los buscaba. */}
      <section className="ld-material__bloque">
        <h4 className="ld-bloque__titulo">
          <IconPaperclip size={13} /> De esta oportunidad
        </h4>
        <p className="ld-material__nota">
          El brief, el contrato, las piezas del cliente. Imágenes, video, documentos —
          todo menos lo que se ejecuta (.exe, .svg, .html y compañía).
        </p>
        <LeadAdjuntos leadId={lead.id} />
      </section>

      {notaDelCatalogo && (
        <section className="ld-material__bloque">
          <h4 className="ld-bloque__titulo"><IconPackage size={13} /> Del catálogo</h4>
          <div className="ld-material__vacio ld-material__vacio--inline">
            {!lead.servicio_id || !servicio ? <IconAlertCircle size={22} /> : <IconPackage size={22} />}
            {notaDelCatalogo}
          </div>
        </section>
      )}

      {!notaDelCatalogo && servicio && (
      <>
      <p className="ld-material__fuente">
        Material de <Link to="/servicios" className="ld-enlace">{servicio.nombre}</Link>
      </p>

      {/* ─── Para el cliente ─── */}
      <section className="ld-material__bloque">
        <h4 className="ld-bloque__titulo">
          Para el cliente <span className="ld-material__conteo">{publicos.length}</span>
        </h4>
        {publicos.length === 0 ? (
          <p className="ld-material__nota">
            Todo el material de este servicio es interno: no hay nada para mandarle al cliente.
          </p>
        ) : (
          <>
            {chats.length === 0 && (
              <p className="ld-material__nota">
                Sin conversación de WhatsApp vinculada — se puede copiar el enlace, pero el envío
                desde acá no está disponible.
              </p>
            )}
            <div className="ld-material__grid">
              {publicos.map(r => <Tarjeta key={r.id} r={r} />)}
            </div>
          </>
        )}
      </section>

      {/* ─── Interno ─── */}
      {internos.length > 0 && (
        <section className="ld-material__bloque ld-material__bloque--interno">
          <h4 className="ld-bloque__titulo">
            Interno <span className="ld-material__conteo">{internos.length}</span>
          </h4>
          <p className="ld-material__nota">
            Guiones y argumentarios del equipo. No se envían al cliente.
          </p>
          <div className="ld-material__grid">
            {internos.map(r => <Tarjeta key={r.id} r={r} interno />)}
          </div>
        </section>
      )}
      </>
      )}

      {/* ─── Confirmación de envío ─── */}
      <Modal
        open={!!aEnviar}
        onClose={() => { if (!enviando) setAEnviar(null) }}
        title="Enviar por WhatsApp"
        footer={
          <>
            <Button variant="secondary" onClick={() => setAEnviar(null)} disabled={enviando}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={enviar} disabled={enviando || !destinoId}>
              {enviando ? 'Enviando...' : 'Enviar'}
            </Button>
          </>
        }
      >
        <div className="form-group">
          <label className="form-label">Chat destino</label>
          <select
            className="form-select"
            value={destinoId ?? ''}
            onChange={e => setDestinoId(Number(e.target.value))}
          >
            {chats.map(c => (
              <option key={c.id} value={c.id}>
                {(c.contacto_id && nombrePorContacto[c.contacto_id]) || c.chat_id}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Mensaje</label>
          <textarea
            className="form-input ld-material__mensaje"
            value={texto}
            onChange={e => setTexto(e.target.value)}
            rows={4}
          />
        </div>
        {error && <div className="ld-material__error">{error}</div>}
      </Modal>
    </div>
  )
}
